import { useEffect, useState } from "react";
import type { AsyncDuckDBConnection } from "@duckdb/duckdb-wasm";
import { arrowTableToObjects, useDebounce } from "@/lib";

// 📦 very small session‑lifetime cache – key = `${tbl}|${col}|${term}`
const CACHE = new Map<string, string[]>();

export function useDistinctAutocomplete(
  conn: AsyncDuckDBConnection | null,
  table: string,
  column: string,
  searchTerm: string,
  {
    contains = true,
    startsWith = false,
    limit = 20,
    debounceMs = 300,
  }: {
    contains?: boolean;
    startsWith?: boolean;
    limit?: number;
    debounceMs?: number;
  } = {},
) {
  const [values, setValues] = useState<string[]>([]);
  const deb = useDebounce(searchTerm, debounceMs);

  useEffect(() => {
    if (!conn || !table || !column) return;
    const key = `${table}|${column}|${deb}`;
    if (CACHE.has(key)) {
      setValues(CACHE.get(key)!);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const safe = deb.replace(/'/g, "''");
        const sample = deb.trim() === "" ? "TABLESAMPLE BERNOULLI(1)" : "";
        const where = deb.trim()
          ? startsWith
            ? `WHERE CAST(\"${column}\" AS TEXT) ILIKE '${safe}%'`
            : contains
            ? `WHERE CAST(\"${column}\" AS TEXT) ILIKE '%${safe}%'`
            : ""
          : "";

        const sql = `SELECT DISTINCT \"${column}\" AS v FROM \"${table}\" ${sample} ${where} ORDER BY \"${column}\" LIMIT ${limit}`;
        const rows = arrowTableToObjects(await conn.query(sql)) as { v: any }[];
        const vals = rows.map((r) => String(r.v));
        CACHE.set(key, vals);
        if (!cancelled) setValues(vals);
      } catch {
        if (!cancelled) setValues([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [conn, table, column, deb, contains, startsWith, limit]);

  return { values };
}
